import axios from 'axios'
import GetJsonData from './dataAccess'

// Notifiable Works options are sourced from the backend lookup table (notifiable_work)
// so the web and mobile clients share a single source of truth.
export const getNotifiableWorks = async (): Promise<string[]> => {
   const data = await GetJsonData('NotifiableWork')
   return (data as string[]).filter(Boolean)
}

const apiURL = process.env.REACT_APP_API_URL

/** A file uploaded on a job for one notifiable work document. */
export interface JobNotifiableWorkFile {
  documentId: number
  fileName: string
}

/** A non-upload document ticked off on a job. */
export interface JobNotifiableWorkCompletion {
  completedDate: string
  completedBy: string | null
}

export interface JobNotifiableWorkDocument {
  notifiableWorkDocumentId: number
  name: string
  isRequired: boolean
  /** Done by uploading a file; otherwise ticked off with a switch. */
  isFileUpload: boolean
  /** The files uploaded for it on this job, oldest first. Empty until one is. */
  files: JobNotifiableWorkFile[]
  /** Null until it has been ticked off on this job. */
  completed: JobNotifiableWorkCompletion | null
  /** Done, by whichever means its type takes. */
  actioned: boolean
}

/** A notifiable work on a job, with its documents and how far they're uploaded. */
export interface JobNotifiableWork {
  name: string
  notifiableWorkId: number | null
  documents: JobNotifiableWorkDocument[]
  /** How many of its documents are required. */
  requiredCount: number
  /** Required documents not yet actioned - what the rail badge counts. */
  outstanding: number
  /** Required documents done - the "5" in "5/10". */
  requiredDone: number
}

/** A notifiable work to choose from on the Notifiable Works screen. */
export interface NotifiableWorkCatalogueEntry {
  notifiableWorkId: number
  name: string
  /** No longer offered, but still on some jobs. */
  isRetired: boolean
  documents: {
    notifiableWorkDocumentId: number
    name: string
    isRequired: boolean
    isFileUpload: boolean
  }[]
}

/** A job with notifiable works, and how far each is done. */
export interface NotifiableWorkJobRow {
  jobId: number
  ref: number
  refFormatted: string
  description: string | null
  client: string | null
  isJobCompleted: boolean
  works: JobNotifiableWork[]
  /** No notifiable work on it has required documents still to do. */
  isComplete: boolean
}

export interface NotifiableWorkOverview {
  works: NotifiableWorkCatalogueEntry[]
  /** Newest job first. */
  jobs: NotifiableWorkJobRow[]
}

/** Everything the Notifiable Works screen shows, in one request. */
export const getNotifiableWorksOverview = async (): Promise<NotifiableWorkOverview> => {
  const response = await axios.get(`https://${apiURL}/NotifiableWork/Overview`, { headers: authHeader() })
  return response.data
}

const authHeader = () => {
  const token = localStorage.getItem('authToken')

  if (!token) {
    throw new Error('Authorization token not found')
  }

  return { Authorization: 'Bearer ' + token }
}

/** The notifiable works on a job ("None" left out), in the order the job lists them. */
export const getJobNotifiableWorks = async (jobId: number): Promise<JobNotifiableWork[]> => {
  const response = await axios.get(`https://${apiURL}/NotifiableWork/Job/${jobId}`, { headers: authHeader() })
  return response.data
}

/** Ticks a non-upload document off on a job, or clears it. */
export const setNotifiableWorkDocumentCompleted = async (
  jobId: number,
  notifiableWorkDocumentId: number,
  completed: boolean
): Promise<JobNotifiableWorkCompletion | null> => {
  const response = await axios.put(
    `https://${apiURL}/NotifiableWork/Job/${jobId}/Documents/${notifiableWorkDocumentId}/Completed`,
    { completed },
    { headers: authHeader() }
  )
  return response.data || null
}

/** Uploads files for one document, added alongside any already there. */
export const uploadNotifiableWorkFiles = async (jobId: number, notifiableWorkDocumentId: number, files: File[]): Promise<void> => {
  const formData = new FormData()
  files.forEach((file) => formData.append('files', file))

  await axios.post(`https://${apiURL}/Files/Job/${jobId}/NotifiableWorkDocument/${notifiableWorkDocumentId}`, formData, {
    headers: authHeader(),
  })
}
